/**
 * 自动性能分析器
 *
 * 提供自动函数包装和采样分析功能，无需手动埋点即可收集性能数据。
 *
 * 支持三种分析模式：
 * 1. 自动包装模式 - 使用 Proxy 自动包装类的所有方法
 * 2. 采样分析模式 - 定时采样调用栈（需要浏览器支持）
 * 3. 装饰器模式 - 使用 @Profile() 装饰器手动标记方法
 */
import { ProfilerSDK } from './ProfilerSDK';
import { ProfileCategory } from './ProfilerTypes';
const DEFAULT_CONFIG = {
    enabled: true,
    sampleInterval: 10,
    minDuration: 0.1, // 0.1ms
    trackAsync: true,
    excludePatterns: [
        /^_/, // 私有方法
        /^get[A-Z]/, // getter 方法
        /^set[A-Z]/, // setter 方法
        /^is[A-Z]/, // 布尔检查方法
        /^has[A-Z]/, // 存在检查方法
    ],
    maxBufferSize: 10000
};
/**
 * 自动性能分析器
 */
export class AutoProfiler {
    constructor(config) {
        this.wrappedObjects = new WeakMap();
        this.samplingProfiler = null;
        this.registeredClasses = new Map();
        this._config = { ...DEFAULT_CONFIG, ...config };
    }
    /**
     * @zh 获取单例实例
     * @en Get singleton instance
     */
    static getInstance(config) {
        if (!AutoProfiler._instance) {
            AutoProfiler._instance = new AutoProfiler(config);
        }
        return AutoProfiler._instance;
    }
    /**
     * @zh 重置实例
     * @en Reset instance
     */
    static resetInstance() {
        if (AutoProfiler._instance) {
            AutoProfiler._instance.dispose();
            AutoProfiler._instance = null;
        }
    }
    /**
     * 启用/禁用自动分析
     */
    static setEnabled(enabled) {
        AutoProfiler.getInstance().setEnabled(enabled);
    }
    /**
     * 注册类以进行自动分析
     * 该类的所有实例方法都会被自动包装
     */
    static registerClass(constructor, category = ProfileCategory.Custom, className) {
        return AutoProfiler.getInstance().registerClass(constructor, category, className);
    }
    /**
     * 包装对象实例的所有方法
     */
    static wrapInstance(instance, className, category = ProfileCategory.Custom) {
        return AutoProfiler.getInstance().wrapInstance(instance, className, category);
    }
    /**
     * 包装单个函数
     */
    static wrapFunction(fn, name, category = ProfileCategory.Custom) {
        return AutoProfiler.getInstance().wrapFunction(fn, name, category);
    }
    /**
     * 启动采样分析器
     */
    static startSampling() {
        AutoProfiler.getInstance().startSampling();
    }
    /**
     * 停止采样分析器
     */
    static stopSampling() {
        return AutoProfiler.getInstance().stopSampling();
    }
    /**
     * @zh 设置启用状态
     * @en Set enabled state
     */
    setEnabled(enabled) {
        this._config.enabled = enabled;
        if (!enabled && this.samplingProfiler) {
            this.samplingProfiler.stop();
        }
    }
    /**
     * @zh 注册类以进行自动分析
     * @en Register class for automatic profiling
     */
    registerClass(constructor, category = ProfileCategory.Custom, className) {
        const name = className || constructor.name;
        this.registeredClasses.set(name, { constructor, category });
        // eslint-disable-next-line @typescript-eslint/no-this-alias -- Required for Proxy construct handler
        const self = this;
        const ProxiedClass = new Proxy(constructor, {
            construct(target, args, newTarget) {
                const instance = Reflect.construct(target, args, newTarget);
                if (self._config.enabled) {
                    self.wrapInstance(instance, name, category);
                }
                return instance;
            }
        });
        return ProxiedClass;
    }
    /**
     * @zh 包装对象实例的所有方法
     * @en Wrap all methods of an object instance
     */
    wrapInstance(instance, className, category = ProfileCategory.Custom) {
        if (!this._config.enabled) {
            return instance;
        }
        // 检查是否已经包装过
        if (this.wrappedObjects.has(instance)) {
            return instance;
        }
        const wrapInfoMap = new Map();
        this.wrappedObjects.set(instance, wrapInfoMap);
        const methodNames = this._getAllMethodNames(instance);
        for (const methodName of methodNames) {
            if (this._shouldExcludeMethod(methodName)) {
                continue;
            }
            const descriptor = this._getPropertyDescriptor(instance, methodName);
            if (!descriptor || typeof descriptor.value !== 'function') {
                continue;
            }
            const original = descriptor.value;
            const wrapped = this._createWrappedMethod(original, className, methodName, category);
            wrapInfoMap.set(methodName, {
                className,
                methodName,
                category,
                original
            });
            try {
                instance[methodName] = wrapped;
            }
            catch {
                // 某些属性可能是只读的
            }
        }
        return instance;
    }
    /**
     * @zh 包装单个函数
     * @en Wrap a single function
     */
    wrapFunction(fn, name, category = ProfileCategory.Custom) {
        if (!this._config.enabled)
            return fn;
        // eslint-disable-next-line @typescript-eslint/no-this-alias -- Required for wrapped function closure
        const self = this;
        const wrapped = function (...args) {
            const handle = ProfilerSDK.beginSample(name, category);
            try {
                const result = fn.apply(this, args);
                if (self._config.trackAsync && result instanceof Promise) {
                    return result.finally(() => {
                        ProfilerSDK.endSample(handle);
                    });
                }
                ProfilerSDK.endSample(handle);
                return result;
            }
            catch (error) {
                ProfilerSDK.endSample(handle);
                throw error;
            }
        };
        Object.defineProperty(wrapped, 'name', { value: fn.name || name });
        Object.defineProperty(wrapped, 'length', { value: fn.length });
        return wrapped;
    }
    /**
     * @zh 启动采样分析器
     * @en Start sampling profiler
     */
    startSampling() {
        if (!this.samplingProfiler) {
            this.samplingProfiler = new SamplingProfiler(this._config);
        }
        this.samplingProfiler.start();
    }
    /**
     * 停止采样分析器
     */
    stopSampling() {
        if (!this.samplingProfiler) {
            return [];
        }
        return this.samplingProfiler.stop();
    }
    /**
     * 释放资源
     */
    dispose() {
        if (this.samplingProfiler) {
            this.samplingProfiler.stop();
            this.samplingProfiler = null;
        }
        this.registeredClasses.clear();
    }
    /**
     * @zh 创建包装后的方法
     * @en Create wrapped method
     */
    _createWrappedMethod(original, className, methodName, category) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias -- Required for wrapped method closure
        const self = this;
        const fullName = `${className}.${methodName}`;
        const minDuration = this._config.minDuration;
        return function (...args) {
            if (!self._config.enabled || !ProfilerSDK.isEnabled()) {
                return original.apply(this, args);
            }
            const startTime = performance.now();
            const handle = ProfilerSDK.beginSample(fullName, category);
            try {
                const result = original.apply(this, args);
                if (self._config.trackAsync && result instanceof Promise) {
                    return result.then((value) => {
                        const duration = performance.now() - startTime;
                        if (duration >= minDuration) {
                            ProfilerSDK.endSample(handle);
                        }
                        return value;
                    }, (error) => {
                        ProfilerSDK.endSample(handle);
                        throw error;
                    });
                }
                const duration = performance.now() - startTime;
                if (duration >= minDuration) {
                    ProfilerSDK.endSample(handle);
                }
                return result;
            }
            catch (error) {
                ProfilerSDK.endSample(handle);
                throw error;
            }
        };
    }
    /**
     * @zh 获取对象的所有方法名
     * @en Get all method names of an object
     */
    _getAllMethodNames(obj) {
        const methods = new Set();
        let current = obj;
        while (current && current !== Object.prototype) {
            for (const name of Object.getOwnPropertyNames(current)) {
                if (name !== 'constructor') {
                    methods.add(name);
                }
            }
            current = Object.getPrototypeOf(current);
        }
        return Array.from(methods);
    }
    /**
     * @zh 获取属性描述符
     * @en Get property descriptor
     */
    _getPropertyDescriptor(obj, name) {
        let current = obj;
        while (current && current !== Object.prototype) {
            const descriptor = Object.getOwnPropertyDescriptor(current, name);
            if (descriptor)
                return descriptor;
            current = Object.getPrototypeOf(current);
        }
        return undefined;
    }
    /**
     * @zh 判断是否应该排除该方法
     * @en Check if method should be excluded
     */
    _shouldExcludeMethod(methodName) {
        if (methodName === 'constructor' || methodName.startsWith('__')) {
            return true;
        }
        for (const pattern of this._config.excludePatterns) {
            if (pattern.test(methodName)) {
                return true;
            }
        }
        return false;
    }
}
AutoProfiler._instance = null;
/**
 * 采样分析器
 * 使用定时器定期采样调用栈信息
 */
class SamplingProfiler {
    constructor(config) {
        this.samples = [];
        this.intervalId = null;
        this.isRunning = false;
        this.config = config;
    }
    /**
     * 开始采样
     */
    start() {
        if (this.isRunning)
            return;
        this.isRunning = true;
        this.samples = [];
        // 使用 requestAnimationFrame 或 setInterval 进行采样
        const sample = () => {
            if (!this.isRunning)
                return;
            const stack = this.captureStack();
            if (stack.length > 0) {
                this.samples.push({
                    timestamp: performance.now(),
                    stack
                });
                // 限制缓冲区大小
                if (this.samples.length > this.config.maxBufferSize) {
                    this.samples.shift();
                }
            }
            // 继续采样
            if (this.config.sampleInterval < 16) {
                // 高频采样使用 setTimeout
                this.intervalId = setTimeout(sample, this.config.sampleInterval);
            }
            else {
                this.intervalId = setTimeout(sample, this.config.sampleInterval);
            }
        };
        sample();
    }
    /**
     * 停止采样并返回数据
     */
    stop() {
        this.isRunning = false;
        if (this.intervalId !== null) {
            clearTimeout(this.intervalId);
            this.intervalId = null;
        }
        return [...this.samples];
    }
    /**
     * 捕获当前调用栈
     */
    captureStack() {
        try {
            // 创建 Error 对象获取调用栈
            const error = new Error();
            const stack = error.stack || '';
            // 解析调用栈
            const lines = stack.split('\n').slice(3); // 跳过 Error 和 captureStack/sample
            const frames = [];
            for (const line of lines) {
                const frame = this.parseStackFrame(line);
                if (frame && !this.isInternalFrame(frame)) {
                    frames.push(frame);
                }
            }
            return frames;
        }
        catch {
            return [];
        }
    }
    /**
     * 解析调用栈帧
     */
    parseStackFrame(line) {
        // Chrome/Edge 格式: "    at functionName (file:line:col)"
        // Firefox 格式: "functionName@file:line:col"
        // Safari 格式: "functionName@file:line:col"
        line = line.trim();
        // Chrome 格式
        let match = line.match(/at\s+(.+?)\s+\(/);
        if (match && match[1]) {
            return match[1];
        }
        // Chrome 匿名函数格式
        match = line.match(/at\s+(.+)/);
        if (match && match[1]) {
            const name = match[1];
            if (!name.includes('(')) {
                return name;
            }
        }
        // Firefox/Safari 格式
        match = line.match(/^(.+?)@/);
        if (match && match[1]) {
            return match[1];
        }
        return null;
    }
    /**
     * 判断是否是内部帧（应该过滤掉）
     */
    isInternalFrame(frame) {
        const internalPatterns = [
            'SamplingProfiler',
            'AutoProfiler',
            'ProfilerSDK',
            'setTimeout',
            'setInterval',
            'requestAnimationFrame',
            '<anonymous>',
            'eval'
        ];
        return internalPatterns.some(pattern => frame.includes(pattern));
    }
}
/**
 * @Profile 装饰器
 * 用于标记需要性能分析的方法
 *
 * @example
 * ```typescript
 * class MySystem extends System {
 *     @Profile()
 *     update() {
 *         // 方法执行时间会被自动记录
 *     }
 *
 *     @Profile('customName', ProfileCategory.Physics)
 *     calculatePhysics() {
 *         // 使用自定义名称和分类
 *     }
 * }
 * ```
 */
export function Profile(name, category = ProfileCategory.Custom) {
    return function (target, propertyKey, descriptor) {
        const original = descriptor.value;
        const methodName = name || `${target.constructor.name}.${String(propertyKey)}`;
        descriptor.value = function (...args) {
            if (!ProfilerSDK.isEnabled()) {
                return original.apply(this, args);
            }
            const handle = ProfilerSDK.beginSample(methodName, category);
            try {
                const result = original.apply(this, args);
                // 处理异步方法
                if (result instanceof Promise) {
                    return result.finally(() => {
                        ProfilerSDK.endSample(handle);
                    });
                }
                // 同步方法，立即结束采样
                ProfilerSDK.endSample(handle);
                return result;
            }
            catch (error) {
                // 发生错误时也要结束采样
                ProfilerSDK.endSample(handle);
                throw error;
            }
        };
        return descriptor;
    };
}
/**
 * @ProfileClass 装饰器
 * 用于自动包装类的所有方法
 *
 * @example
 * ```typescript
 * @ProfileClass(ProfileCategory.Physics)
 * class PhysicsSystem extends System {
 *     update() { ... }      // 自动被包装
 *     calculate() { ... }   // 自动被包装
 * }
 * ```
 */
export function ProfileClass(category = ProfileCategory.Custom) {
    return function (constructor) {
        return AutoProfiler.registerClass(constructor, category);
    };
}
//# sourceMappingURL=AutoProfiler.js.map